/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { BulkMaintenanceResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type BulkEnableMaintenanceModeMutationConfig = {
  onSuccess?: (data: BulkMaintenanceResponse, variables: BulkEnableMaintenanceModeParams) => void;
  onError?: (error: Error, variables: BulkEnableMaintenanceModeParams) => void;
  onSettled?: (
    data: BulkMaintenanceResponse | undefined,
    error: Error | null,
    variables: BulkEnableMaintenanceModeParams,
  ) => void;
  onMutate?: (variables: BulkEnableMaintenanceModeParams) => void;
};

export type BulkEnableMaintenanceModeParams = {
  nodeIds: string[];
};

/**
 * Hook for bulk enabling maintenance mode on multiple nodes.
 *
 * Returns the maintenance config ID for each node without assigning them.
 * Use the bulk assign endpoint to apply the configs.
 *
 * API Endpoint:
 * - PUT /tenants/:tenantId/nodes/maintenance - Bulk enable maintenance mode
 *
 * @param config.onSuccess - Called after successful maintenance mode enable
 * @param config.onError - Called if enable fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 * @param config.onMutate - Called before mutation starts
 *
 * @returns React Query mutation result for bulk enabling maintenance mode
 */
export function useBulkEnableMaintenanceModeMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: BulkEnableMaintenanceModeMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutateAsync, isPending, isError, error } = useMutation<
    BulkMaintenanceResponse,
    Error,
    BulkEnableMaintenanceModeParams
  >({
    mutationFn: async ({ nodeIds }: BulkEnableMaintenanceModeParams) => {
      return mgmtPlaneAPIClient.nodes.bulkEnableMaintenanceMode({
        tenantId: tenantId!,
        requestBody: {
          nodes: nodeIds.map((nodeId) => ({ nodeId })),
        },
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutateAsync, isPending, isError, error };
}
